/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from 'react-router';
import { useWizardNavigation } from "./hooks/use-wizard-navigation";
import { useStepUrlSync } from "./steps-context.url-sync";
import { useFormTiming } from "@shared/ui/components/form/use-form-timing";
import type { Step, StepsContext, StepsProviderProps } from "./steps-context.types";

export type { Step, StepsContext, StepsProviderProps } from "./steps-context.types";

const StepsContextInstance = createContext<StepsContext | undefined>(undefined);

const StepsProvider: ComponentWithChildren<StepsProviderProps> = ({
  initialStepIdOrder,
  firstStep,
  initialSteps,
  children,
  wizardId,
  enableUrlNavigation = false,
  stepParamName = "step",
  lockedStepId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStepId, setCurrentStepIdState] = useState(() => {
    if (lockedStepId) {
      return lockedStepId;
    }
    if (enableUrlNavigation) {
      const urlStep = searchParams.get(stepParamName);
      if (urlStep && initialStepIdOrder.includes(urlStep)) {
        return urlStep;
      }
    }
    return firstStep ?? initialStepIdOrder[0];
  });
  const [steps, setSteps] = useState<Record<string, Step>>(initialSteps);
  const [stepIdOrder, setStepIdOrder] = useState(initialStepIdOrder);

  // Guard: skip URL sync effect when setCurrentStepId already pushed
  const urlSyncedBySetterRef = useRef(false);

  // When lockedStepId activates, force state and URL to match
  useEffect(() => {
    if (lockedStepId && currentStepId !== lockedStepId) {
      setCurrentStepIdState(lockedStepId);
      if (enableUrlNavigation) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(stepParamName, lockedStepId);
          return next;
        }, { replace: true });
      }
    }
  }, [lockedStepId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapper for setCurrentStepId that also updates URL when URL navigation is enabled
  const setCurrentStepId: Dispatch<SetStateAction<string>> = useCallback(
    (stepId: SetStateAction<string>) => {
      const newStepId = typeof stepId === 'function'
        ? stepId(currentStepId)
        : stepId;
      
      if (lockedStepId && newStepId !== lockedStepId) {
        return;
      }

      if (enableUrlNavigation && stepIdOrder.includes(newStepId)) {
        urlSyncedBySetterRef.current = true;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(stepParamName, newStepId);
          return next;
        });
      }
      setCurrentStepIdState(newStepId);
    },
    [enableUrlNavigation, stepIdOrder, stepParamName, setSearchParams, currentStepId, lockedStepId]
  );

  // Memoized onStepChange for useWizardNavigation — prevents effect re-runs
  const handleWizardStepChange = useCallback(
    (stepId: string) => {
      if (lockedStepId && stepId !== lockedStepId) return;
      setCurrentStepIdState(stepId);
    },
    [lockedStepId]
  );

  // Set up URL navigation if enabled
  const navigation = useWizardNavigation({
    wizardId: wizardId || "default",
    currentStepId,
    stepIdOrder,
    onStepChange: handleWizardStepChange,
    stepParamName,
    disabled: !enableUrlNavigation || !!lockedStepId,
  });

  // Form open->submit timing: t0 is this provider's mount; the owning wizard
  // calls markSubmitted on submit-success to emit a form_timing event.
  const { markSubmitted } = useFormTiming();

  // Update currentStepId when URL changes (if URL navigation is enabled)
  useStepUrlSync({
    enableUrlNavigation,
    searchParams,
    setSearchParams,
    stepParamName,
    stepIdOrder,
    currentStepId,
    lockedStepId,
    setCurrentStepIdState,
  });

  const initialValues = useMemo(
    () => ({
      currentStepId,
      setCurrentStepId,
      stepIdOrder,
      setStepIdOrder,
      steps,
      setSteps,
      initialStepIdOrder,
      firstStep,
      wizardId,
      markSubmitted,
      ...(enableUrlNavigation && navigation),
    }),
    [
      currentStepId,
      setCurrentStepId,
      stepIdOrder,
      setStepIdOrder,
      steps,
      setSteps,
      initialStepIdOrder,
      firstStep,
      wizardId,
      markSubmitted,
      enableUrlNavigation,
      lockedStepId,
      navigation,
    ]
  );

  return (
    <StepsContextInstance.Provider value={initialValues}>
      {children}
    </StepsContextInstance.Provider>
  );
};

/**
 * useStepsContext function.
 *
 * @returns {*} Return value description
 */
export const useStepsContext = () => {
  const context = useContext(StepsContextInstance);
  if (!context)
    throw new Error("This component must be wrapped by StepsContext");
  return context;
};

type UseStepState = (
  stepId: string,
  stepsContext: StepsContext
) => "current" | "history" | "future";
export const useStepState: UseStepState = (stepId, stepsContext) => {
  const { stepIdOrder, currentStepId } = stepsContext;
  const currentIndex = stepIdOrder.indexOf(currentStepId);
  const stepIdIndex = stepIdOrder.indexOf(stepId);
  if (stepIdIndex === -1)
    throw new Error("'stepId' not found in context 'stepIdOrder'");
  if (stepIdIndex > currentIndex) return "future";
  if (stepIdIndex < currentIndex) return "history";
  return "current";
};

export default StepsProvider;
